import React, { useState, useRef, useEffect } from 'react';
import type { RoomLayout } from '../../../data-transformer/index.ts';
import type { ValidateResult } from 'discriminator';
import { styles } from '../styles.ts';

import type { Zone, ZoneAnchorObject } from '../../../zoning/index.ts';
import { ZONE_DEFINITIONS } from '../../../zoning/config.ts';

interface RoomPreviewProps {
  roomData: RoomLayout;
  validationResult: ValidateResult | null;
  isValidating: boolean;
  onSaveRoom: () => void;
  onSolveError: (repairInstructions: string, impactedObjectId?: string, rule?: any) => void;
  onSolveAllErrors: () => void;
  status: 'idle' | 'loading' | 'success' | 'error';
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  zones?: Zone[];
  showZonesInPreview?: boolean;
  focusedZoneId?: string | null;
  zoneAnchors?: ZoneAnchorObject[];
  showZoneAnchors?: boolean;
  focusedAnchorId?: string | null;
}

export function RoomPreview({
  roomData,
  validationResult,
  isValidating,
  onSaveRoom,
  onSolveError,
  onSolveAllErrors,
  status,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  zones = [],
  showZonesInPreview = true,
  focusedZoneId,
  zoneAnchors = [],
  showZoneAnchors = true,
  focusedAnchorId
}: RoomPreviewProps) {
  // SVG drawing math
  const METER_TO_PX = 80;
  const centerX = 500;
  const centerY = 300;

  const roomWidthPx = roomData.width * METER_TO_PX;
  const roomLengthPx = roomData.length * METER_TO_PX;
  const roomLeft = centerX - roomWidthPx / 2;
  const roomTop = centerY - roomLengthPx / 2;

  // Zoom / Pan states
  const [zoom, setZoom] = useState(1.0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  // Separate accordion states - both collapsed (closed) by default
  const [valAccordionOpen, setValAccordionOpen] = useState(false);
  const [coordAccordionOpen, setCoordAccordionOpen] = useState(false);

  // Ref to canvas container for native wheel event listener
  const canvasRef = useRef<HTMLDivElement>(null);

  // Auto-reset zoom/pan when room dimensions change
  useEffect(() => {
    setZoom(1.0);
    setPan({ x: 0, y: 0 });
  }, [roomData.width, roomData.length]);

  // Bind wheel events natively to support preventDefault() inside non-passive listener
  useEffect(() => {
    const canvasElement = canvasRef.current;
    if (!canvasElement) return;

    const handleNativeWheel = (e: WheelEvent) => {
      // Prevent standard browser page scroll
      e.preventDefault();
      
      if (e.deltaY < 0) {
        setZoom(prev => Math.min(prev * 1.1, 5.0)); // Zoom In
      } else {
        setZoom(prev => Math.max(prev / 1.1, 0.2)); // Zoom Out
      }
    };

    canvasElement.addEventListener('wheel', handleNativeWheel, { passive: false });
    return () => {
      canvasElement.removeEventListener('wheel', handleNativeWheel);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only pan with left click
    setIsDragging(true);
    dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPan({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 5.0));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.2));
  };

  const handleReset = () => {
    setZoom(1.0);
    setPan({ x: 0, y: 0 });
  };

  // Determine status color badge for validation header
  const getValidationSummaryText = () => {
    if (isValidating) return 'Checking layout constraints...';
    if (!validationResult) return 'No validation results';
    
    const { passed, failed, warnings } = validationResult.summary;
    return `${passed} passed, ${failed} failed, ${warnings} warnings`;
  };

  const getValidationBadgeStyle = () => {
    if (isValidating) return { color: '#f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.1)' };
    if (!validationResult) return { color: 'var(--text-muted)', backgroundColor: 'rgba(255, 255, 255, 0.05)' };
    
    const { failed, warnings } = validationResult.summary;
    if (failed > 0) {
      return { color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)' };
    }
    if (warnings > 0) {
      return { color: '#f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.1)' };
    }
    return { color: 'var(--success)', backgroundColor: 'rgba(46, 125, 50, 0.1)' };
  };

  // Sort rules: failed first, then warning, then pass
  const sortedRules = validationResult?.results
    ? [...validationResult.results].sort((a, b) => {
        const order = { fail: 0, warning: 1, pass: 2 };
        const scoreA = order[a.status as 'fail' | 'warning' | 'pass'] ?? 3;
        const scoreB = order[b.status as 'fail' | 'warning' | 'pass'] ?? 3;
        return scoreA - scoreB;
      })
    : [];

  return (
    <div style={styles.sideBySideContainer}>
      {/* Left Column: Room Layout Preview SVG and Furniture placements */}
      <div style={styles.leftColumn}>
        <section style={{ ...styles.card, flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-main)', margin: 0 }}>Room Layout Preview</h2>
            
            {/* Navigation History & Save Action Bar */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button 
                onClick={onUndo} 
                disabled={!canUndo} 
                style={{ 
                  ...styles.fileUploadBtn, 
                  padding: '6px 12px',
                  opacity: canUndo ? 1 : 0.3,
                  cursor: canUndo ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Go to previous layout (Back)"
              >
                ◀ Back
              </button>
              <button 
                onClick={onRedo} 
                disabled={!canRedo} 
                style={{ 
                  ...styles.fileUploadBtn, 
                  padding: '6px 12px',
                  opacity: canRedo ? 1 : 0.3,
                  cursor: canRedo ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Go to next layout (Forward)"
              >
                Forward ▶
              </button>

              <button 
                onClick={onSaveRoom} 
                disabled={status === 'loading'} 
                style={{ 
                  ...styles.saveButton,
                  opacity: status === 'loading' ? 0.6 : 1 
                }}
              >
                {status === 'loading' ? 'Saving...' : 'Save and View'}
              </button>
            </div>
          </div>
          
          {/* Canvas container with scroll event and grab styles */}
          <div 
            ref={canvasRef}
            style={{
              ...styles.canvasContainer,
              ...(isDragging ? styles.canvasContainerDragging : {})
            }}
          >
            <svg 
              viewBox="0 0 1000 600" 
              width="100%" 
              height="100%"
              style={styles.svg}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {/* Definitions */}
              <defs>
                <pattern id="room-grid" width={METER_TO_PX} height={METER_TO_PX} patternUnits="userSpaceOnUse">
                  <path d={`M ${METER_TO_PX} 0 L 0 0 0 ${METER_TO_PX}`} fill="none" stroke="rgba(255, 255, 255, 0.05)" strokeWidth="1"/>
                </pattern>
              </defs>

              {/* Background overlay supporting click to drag */}
              <rect width="1000" height="600" fill="transparent" />

              {/* Centered Zoom/Pan Group */}
              <g transform={`translate(${pan.x + centerX}, ${pan.y + centerY}) scale(${zoom}) translate(${-centerX}, ${-centerY})`}>
                {/* Room Floor Grid */}
                <rect 
                  x={roomLeft} 
                  y={roomTop} 
                  width={roomWidthPx} 
                  height={roomLengthPx} 
                  fill="url(#room-grid)" 
                />
                
                {/* Room Outer Wall */}
                <rect 
                  x={roomLeft} 
                  y={roomTop} 
                  width={roomWidthPx} 
                  height={roomLengthPx} 
                  fill="none" 
                  stroke="var(--border-color)" 
                  strokeWidth="4" 
                  rx="4"
                />

                {/* Intelligent Zoning Polygons Layer */}
                {showZonesInPreview && zones.map((zone) => {
                  if (!zone.polygon || zone.polygon.length < 3) return null;
                  
                  const isFocused = focusedZoneId === zone.id;
                  const pointsStr = zone.polygon
                    .map(p => `${centerX + (p[0] / 1000) * METER_TO_PX},${centerY + (p[1] / 1000) * METER_TO_PX}`)
                    .join(' ');

                  const centroidX = centerX + (zone.centroid[0] / 1000) * METER_TO_PX;
                  const centroidY = centerY + (zone.centroid[1] / 1000) * METER_TO_PX;

                  return (
                    <g key={zone.id} className="zone-polygon-group">
                      {/* Polygon Surface */}
                      <polygon
                        points={pointsStr}
                        fill={zone.color}
                        stroke={zone.strokeColor}
                        strokeWidth={isFocused ? '3' : '2'}
                        strokeDasharray={isFocused ? 'none' : '8 4'}
                        opacity={isFocused ? 1.0 : 0.85}
                        style={{ transition: 'all 0.3s ease' }}
                      />

                      {/* Zone Centroid Badge */}
                      <g transform={`translate(${centroidX}, ${centroidY}) scale(${1 / zoom}) translate(${-centroidX}, ${-centroidY})`}>
                        {/* Pill background */}
                        <rect
                          x={centroidX - 60}
                          y={centroidY - 18}
                          width={120}
                          height={36}
                          rx={8}
                          fill="rgba(24, 24, 27, 0.88)"
                          stroke={zone.strokeColor}
                          strokeWidth={isFocused ? '2' : '1'}
                        />
                        {/* Zone Name */}
                        <text
                          x={centroidX}
                          y={centroidY - 4}
                          fill="#f3f4f6"
                          fontSize="11"
                          fontWeight="700"
                          textAnchor="middle"
                          alignmentBaseline="middle"
                        >
                          {zone.name}
                        </text>
                        {/* Zone Area Metrics */}
                        <text
                          x={centroidX}
                          y={centroidY + 9}
                          fill={zone.strokeColor}
                          fontSize="9"
                          fontWeight="600"
                          textAnchor="middle"
                          alignmentBaseline="middle"
                        >
                          {zone.area} m² ({zone.areaPercent}%)
                        </text>
                      </g>
                    </g>
                  );
                })}

                {/* Spatial Anchor Objects Layer (Anchors used to generate zones) */}
                {showZoneAnchors && zoneAnchors.map((anchor) => {
                  const width = anchor.size?.width ?? 1;
                  const length = anchor.size?.length ?? 1;
                  const posX = anchor.position?.x ?? 0;
                  const posY = anchor.position?.y ?? 0;
                  const angle = anchor.rotationDegrees ?? 0;

                  const widthPx = width * METER_TO_PX;
                  const lengthPx = length * METER_TO_PX;
                  const cx = roomLeft + posX * METER_TO_PX;
                  const cy = roomTop + posY * METER_TO_PX;
                  const xPx = cx - widthPx / 2;
                  const yPx = cy - lengthPx / 2;

                  const zoneDef = ZONE_DEFINITIONS[anchor.zoneType] || ZONE_DEFINITIONS.living;
                  const isFocused = (focusedZoneId === anchor.zoneId) || (focusedAnchorId === anchor.id);

                  return (
                    <g 
                      key={anchor.id}
                      transform={`rotate(${-angle}, ${cx}, ${cy})`}
                      className="zone-anchor-group"
                      style={{ pointerEvents: 'none' }}
                    >
                      {/* Anchor Footprint Rectangle */}
                      <rect 
                        x={xPx} 
                        y={yPx} 
                        width={widthPx} 
                        height={lengthPx} 
                        fill={zoneDef.color} 
                        stroke={zoneDef.strokeColor} 
                        strokeWidth={isFocused ? '3' : '2'} 
                        strokeDasharray={isFocused ? 'none' : '6 3'}
                        rx="6"
                        opacity={isFocused ? 0.95 : 0.75}
                      />

                      {/* Anchor Badge Icon + Name */}
                      <g transform={`translate(${cx}, ${cy}) scale(${1 / zoom}) translate(${-cx}, ${-cy})`}>
                        <rect
                          x={cx - 65}
                          y={cy - 13}
                          width={130}
                          height={26}
                          rx={6}
                          fill="rgba(15, 23, 42, 0.94)"
                          stroke={zoneDef.strokeColor}
                          strokeWidth="1.5"
                        />
                        <text 
                          x={cx} 
                          y={cy} 
                          fill="#f8fafc" 
                          fontSize="10" 
                          fontWeight="700"
                          textAnchor="middle" 
                          alignmentBaseline="middle"
                        >
                          ⚓ {anchor.name}
                        </text>
                      </g>
                      {/* Dimension and Virtual Tag */}
                      <g transform={`translate(${cx}, ${cy + 19}) scale(${1 / zoom}) translate(${-cx}, ${-(cy + 19)})`}>
                        <text 
                          x={cx} 
                          y={cy + 19} 
                          fill={zoneDef.strokeColor} 
                          fontSize="8.5" 
                          fontWeight="700"
                          textAnchor="middle"
                        >
                          [{width.toFixed(1)}×{length.toFixed(1)}m] {anchor.isVirtualAnchor ? '(Kotva)' : ''}
                        </text>
                      </g>
                    </g>
                  );
                })}

                {/* Room Items */}
                {roomData.items.map((item) => {
                  const width = item.size?.width ?? 1;
                  const length = item.size?.length ?? 1;
                  const posX = item.position?.x ?? 0;
                  const posY = item.position?.y ?? 0;
                  const angle = item.angle ?? 0;

                  const widthPx = width * METER_TO_PX;
                  const lengthPx = length * METER_TO_PX;
                  const cx = roomLeft + posX * METER_TO_PX;
                  const cy = roomTop + posY * METER_TO_PX;
                  const xPx = cx - widthPx / 2;
                  const yPx = cy - lengthPx / 2;
                  
                  return (
                    <g 
                      key={item.itemId}
                      transform={`rotate(${-angle}, ${cx}, ${cy})`}
                    >
                      {/* Footprint Rectangle */}
                      <rect 
                        x={xPx} 
                        y={yPx} 
                        width={widthPx} 
                        height={lengthPx} 
                        fill="rgba(156, 39, 176, 0.15)" 
                        stroke="var(--accent)" 
                        strokeWidth="2" 
                        rx="4"
                      />
                      
                      {/* Front orientation line (red bar on the bottom side) */}
                      <line 
                        x1={xPx} 
                        y1={yPx + lengthPx} 
                        x2={xPx + widthPx} 
                        y2={yPx + lengthPx} 
                        stroke="#ef4444" 
                        strokeWidth="3" 
                      />

                      {/* Labeled name */}
                      <g transform={`translate(${cx}, ${cy}) scale(${1 / zoom}) translate(${-cx}, ${-cy})`}>
                        <text 
                          x={cx} 
                          y={cy} 
                          fill="var(--text-main)" 
                          fontSize="11" 
                          fontWeight="600"
                          textAnchor="middle" 
                          alignmentBaseline="middle"
                        >
                          {item.label}
                        </text>
                      </g>
                      {/* Accessory Type */}
                      <g transform={`translate(${cx}, ${cy + 14}) scale(${1 / zoom}) translate(${-cx}, ${-(cy + 14)})`}>
                        <text 
                          x={cx} 
                          y={cy + 14} 
                          fill="var(--text-muted)" 
                          fontSize="9" 
                          textAnchor="middle"
                        >
                          ({item.type})
                        </text>
                      </g>
                    </g>
                  );
                })}
              </g>
            </svg>

            {/* Floating Zoom overlay controls */}
            <div style={styles.canvasControls}>
              <button onClick={handleZoomIn} title="Zoom In" style={styles.canvasBtn}>+</button>
              <button onClick={handleZoomOut} title="Zoom Out" style={styles.canvasBtn}>−</button>
              <button onClick={handleReset} title="Reset view" style={{ ...styles.canvasBtn, fontSize: '11px', fontWeight: 600 }}>Reset</button>
            </div>
          </div>

          {/* Accordion 2: Furniture List / Coordinates - Collapsed by default */}
          <div style={styles.accordion}>
            <div 
              onClick={() => setCoordAccordionOpen(!coordAccordionOpen)} 
              style={styles.accordionHeader}
            >
              <div style={styles.accordionTitle}>
                <span>🛋️ Furniture Placements ({roomData.items.length} items)</span>
              </div>
              <span style={{
                ...styles.accordionArrow,
                transform: coordAccordionOpen ? 'rotate(90deg)' : 'rotate(0deg)'
              }}>
                ▶
              </span>
            </div>

            {coordAccordionOpen && (
              <div style={styles.accordionContent}>
                {roomData.items.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic' }}>
                    No items placed in this room.
                  </div>
                ) : (
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Item ID</th>
                        <th style={styles.th}>Label (Type)</th>
                        <th style={styles.th}>Size (W x L x H)</th>
                        <th style={styles.th}>Position (X, Y, Z)</th>
                        <th style={styles.th}>Rotation (°)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roomData.items.map((item) => (
                        <tr key={item.itemId} style={styles.tr}>
                          <td style={styles.td}><code style={styles.code}>{item.itemId}</code></td>
                          <td style={styles.td}>{item.label} <span style={styles.typeSpan}>({item.type || 'unknown'})</span></td>
                          <td style={styles.td}>
                            {(item.size?.width ?? 0).toFixed(2)}m × {(item.size?.length ?? 0).toFixed(2)}m × {(item.size?.height ?? 0).toFixed(2)}m
                          </td>
                          <td style={styles.td}>
                            {(item.position?.x ?? 0).toFixed(2)}m, {(item.position?.y ?? 0).toFixed(2)}m, {(item.position?.z ?? 0).toFixed(2)}m
                          </td>
                          <td style={styles.td}>{(item.angle ?? 0).toFixed(1)}°</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Right Column: Discriminator Validation Summary */}
      <div style={styles.rightColumn}>
        <section style={{ ...styles.card, flex: 1, boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-main)', margin: 0 }}>Discriminator Validation Summary</h2>
            <span style={{ 
              ...styles.statusBadge,
              ...getValidationBadgeStyle(),
              fontSize: '11px'
            }}>
              {getValidationSummaryText()}
            </span>
          </div>

          {validationResult ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
              {/* Overall metric cards (Always Visible) */}
              <div style={styles.validationSummaryGrid}>
                <div style={styles.validationCard}>
                  <div style={styles.valNumber}>{validationResult.summary.passRate.toFixed(0)}%</div>
                  <div style={styles.valLabel}>Pass Rate</div>
                </div>
                <div style={styles.validationCard}>
                  <div style={{ ...styles.valNumber, color: 'var(--success)' }}>{validationResult.summary.passed}</div>
                  <div style={styles.valLabel}>Passed</div>
                </div>
                <div style={styles.validationCard}>
                  <div style={{ ...styles.valNumber, color: '#ef4444' }}>{validationResult.summary.failed}</div>
                  <div style={styles.valLabel}>Failed</div>
                </div>
                <div style={styles.validationCard}>
                  <div style={{ ...styles.valNumber, color: '#f59e0b' }}>{validationResult.summary.warnings}</div>
                  <div style={styles.valLabel}>Warnings</div>
                </div>
              </div>

              {/* Solve All Errors button (Visible when failed rules count > 0) */}
              {validationResult.summary.failed > 0 && (
                <button
                  type="button"
                  onClick={onSolveAllErrors}
                  style={styles.solveAllBtn}
                >
                  ⚡ Solve All Errors
                </button>
              )}

              {/* Accordion for individual rules list - Collapsed by default */}
              <div style={{ ...styles.accordion, marginTop: 0 }}>
                <div 
                  onClick={() => setValAccordionOpen(!valAccordionOpen)} 
                  style={styles.accordionHeader}
                >
                  <div style={styles.accordionTitle}>
                    <span>📋 Rule Verification Details</span>
                  </div>
                  <span style={{
                    ...styles.accordionArrow,
                    transform: valAccordionOpen ? 'rotate(90deg)' : 'rotate(0deg)'
                  }}>
                    ▶
                  </span>
                </div>

                {valAccordionOpen && (
                  <div style={{
                    ...styles.accordionContent,
                    maxHeight: '450px',
                    overflowY: 'auto'
                  }}>
                    <div style={styles.rulesList}>
                      {sortedRules.map((rule: any, idx: number) => (
                        <div key={idx} style={{ 
                          ...styles.ruleItem,
                          borderLeft: `4px solid ${
                            rule.status === 'pass' ? 'var(--success)' : 
                            rule.status === 'fail' ? '#ef4444' : '#f59e0b'
                          }`
                        }}>
                          <div style={styles.ruleHeader}>
                            <span style={styles.ruleId}>{rule.ruleId}</span>
                            <span style={{ 
                              ...styles.statusBadge,
                              color: rule.status === 'pass' ? 'var(--success)' : 
                                     rule.status === 'fail' ? '#ef4444' : '#f59e0b',
                              backgroundColor: rule.status === 'pass' ? 'rgba(46, 125, 50, 0.1)' : 
                                               rule.status === 'fail' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)'
                            }}>
                              {rule.status.toUpperCase()}
                            </span>
                          </div>
                          <div style={styles.ruleMsg}>{rule.userMessage}</div>
                          {rule.status !== 'pass' && rule.userRepairInstructions && (
                            <div style={styles.repairInfo}>
                              <span><strong>Fix Recommendation:</strong> {rule.userRepairInstructions}</span>
                              {rule.status === 'fail' && (
                                <button 
                                  type="button" 
                                  onClick={() => onSolveError(rule.userRepairInstructions, rule.impactedObjectId, rule)}
                                  style={styles.solveBtn}
                                >
                                  Solve
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic' }}>
              {isValidating ? 'Checking layout constraints...' : 'No active verification results. Perform a layout load or change to trigger.'}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
